// @ts-check
// Formulários do caminho e do lead (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): adicionar a
// outro fluxo, remover de outros fluxos (que também bloqueia a entrada neles até este terminar, K28), mudar a etapa
// (Matriculado fica de fora; Perdido e Não é lead pedem o motivo), etiquetas (até 15) e o responsável fixo ou em
// rodízio.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { workspaceMembersState } from '../../../data/workspace-members.js';
import { Checkbox } from '../../../ui/feedback.js';
import { Tag } from '../../../ui/chip.js';
import { normalizeSearchText } from '../../../lib/utils.js';
import {
  FLOW_BLOCKED_TARGET_STAGES, FLOW_STAGE_REASON_FIELDS,
} from '../../../../../shared/flows/flow-definition.js';
import { MAXIMUM_ADD_TO_FLOW_TARGETS, MAXIMUM_ASSIGN_OWNER_MEMBERS, MAXIMUM_FLOW_TAGS_PER_ACTION } from '../../../../../shared/flows/flow-limits.js';
import { cardField, memberDisplayName, stageOptions } from '../flow-lookups.js';
import { FlowField, FlowNotice, fieldProblemText, flowProblemText } from '../flow-ui.js';
import { FlowRadioOption } from '../flow-lifecycle-dialogs.js';
import { translateMetadataLabel, t } from '../../../i18n/index.js';

/**
 * @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps
 * @typedef {import('../../../lib/flow-list.js').FlowSummary} FlowSummary
 */

/**
 * Lista de escolha com caixas (fluxos, membros), com busca quando passa de oito itens.
 * @param {{ items: Array<{ id: string, label: string, badge?: string | null }>, selected: string[], onChange: (ids: string[]) => void,
 *   maximum?: number, label: string, emptyText?: string }} props
 */
export function FlowChecklist({ items, selected, onChange, maximum, label, emptyText }) {
  const [search, setSearch] = useState('');
  const needle = normalizeSearchText(search);
  const visible = needle ? items.filter((item) => normalizeSearchText(item.label).includes(needle)) : items;
  const isFull = maximum !== undefined && selected.length >= maximum;
  if (items.length === 0) return html`<p class="flow-field-hint">${emptyText ?? ''}</p>`;
  return html`<div class="flow-checklist">
    ${items.length > 8
      ? html`<input type="search" class="text-input flow-checklist-search" value=${search} placeholder=${t('flows.forms.search')} aria-label=${t('flows.forms.searchIn', { label })}
          onInput=${(/** @type {InputEvent} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />`
      : null}
    <ul class="flow-checklist-items" role="group" aria-label=${label}>
      ${visible.map((item) => {
        const isChecked = selected.includes(item.id);
        return html`<li key=${item.id}>
          <label class="flow-checkbox-row" data-disabled=${!isChecked && isFull ? 'true' : undefined}>
            <${Checkbox} checked=${isChecked} disabled=${!isChecked && isFull} ariaLabel=${item.label}
              onChange=${(/** @type {boolean} */ value) => onChange(value ? [...selected, item.id] : selected.filter((id) => id !== item.id))} />
            <span>${item.label}</span>
            ${item.badge ? html`<span class="flow-chip" data-tone="gray">${item.badge}</span>` : null}
          </label>
        </li>`;
      })}
    </ul>
    ${maximum !== undefined ? html`<span class="flow-field-hint">${t('flows.forms.counter', { count: selected.length, maximum })}</span>` : null}
  </div>`;
}

/** Outros fluxos, ativos primeiro, com o selo "Inativo"/"Rascunho". @param {FlowSummary[] | null} flows @param {string} flowId */
function otherFlowItems(flows, flowId) {
  const order = /** @type {Record<string, number>} */ ({ ACTIVE: 0, INACTIVE: 1, DRAFT: 2 });
  return (flows ?? []).filter((flow) => flow.id !== flowId)
    .sort((first, second) => (order[first.status] ?? 3) - (order[second.status] ?? 3) || first.name.localeCompare(second.name, 'pt-BR'))
    .map((flow) => ({ id: flow.id, label: flow.name, badge: flow.status === 'ACTIVE' ? null : t(`flows.status.${flow.status}`) }));
}

/** Pendências de alvo (fluxo que sumiu ou o próprio fluxo). @param {NodeFormProps['problems']} problems */
const targetProblems = (problems) => problems.filter((problem) => problem.code === 'FLOW_TARGET_SELF' || problem.code === 'FLOW_TARGET_UNAVAILABLE' || problem.code === 'LIMIT_ITEMS');

/** @param {NodeFormProps} props */
export function AddToFlowForm({ node, flowId, problems, lookupData, onConfig }) {
  const config = node.config;
  const extra = targetProblems(problems)[0];
  return html`<div class="flow-node-form" data-testid="flow-add-to-flow-form">
    <${FlowField} label=${t('flows.forms.addToFlow.flows')} error=${fieldProblemText(problems, 'flowIds') ?? (extra ? flowProblemText(extra) : null)}>
      <${FlowChecklist} items=${otherFlowItems(lookupData.flows, flowId)} selected=${config.flowIds ?? []} maximum=${MAXIMUM_ADD_TO_FLOW_TARGETS}
        label=${t('flows.forms.addToFlow.flows')} emptyText=${t('flows.forms.addToFlow.noFlows')} onChange=${(/** @type {string[]} */ flowIds) => onConfig({ flowIds })} />
    <//>
    <label class="flow-checkbox-row">
      <${Checkbox} checked=${config.allowReentry === true} onChange=${(/** @type {boolean} */ allowReentry) => onConfig({ allowReentry })} ariaLabel=${t('flows.forms.addToFlow.allowReentry')} />
      <span>${t('flows.forms.addToFlow.allowReentry')}</span>
    </label>
    <${FlowNotice} icon="IconInfoCircle">${t('flows.forms.addToFlow.notice')}<//>
  </div>`;
}

/** @param {NodeFormProps} props */
export function RemoveFromFlowsForm({ node, flowId, problems, lookupData, onConfig, onReplaceConfig }) {
  const config = node.config;
  const extra = targetProblems(problems)[0];
  const name = `flow-remove-mode-${node.id}`;
  return html`<div class="flow-node-form" data-testid="flow-remove-from-flows-form">
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.nodes.REMOVE_FROM_FLOWS.name')}>
      <${FlowRadioOption} name=${name} value="SELECTED" checked=${config.mode !== 'ALL_OTHERS'} title=${t('flows.forms.removeFromFlows.selected')}
        onSelect=${() => onReplaceConfig({ mode: 'SELECTED', flowIds: [] })} />
      <${FlowRadioOption} name=${name} value="ALL_OTHERS" checked=${config.mode === 'ALL_OTHERS'} title=${t('flows.forms.removeFromFlows.allOthers')}
        onSelect=${() => onReplaceConfig({ mode: 'ALL_OTHERS', flowIds: [] })} />
    </div>
    ${config.mode !== 'ALL_OTHERS'
      ? html`<${FlowField} label=${t('flows.forms.removeFromFlows.flows')} error=${fieldProblemText(problems, 'flowIds') ?? (extra ? flowProblemText(extra) : null)}>
          <${FlowChecklist} items=${otherFlowItems(lookupData.flows, flowId)} selected=${config.flowIds ?? []} label=${t('flows.forms.removeFromFlows.flows')}
            emptyText=${t('flows.forms.addToFlow.noFlows')} onChange=${(/** @type {string[]} */ flowIds) => onConfig({ flowIds })} />
        <//>`
      : null}
    <${FlowNotice} icon="IconLogout">${t('flows.forms.removeFromFlows.notice')}<//>
  </div>`;
}

/** Opções de um campo de seleção do card, traduzidas. @param {string} fieldName */
function selectOptions(fieldName) {
  return (cardField(fieldName)?.options ?? []).map((option) => ({ value: option.value, label: translateMetadataLabel(option.label), color: option.color ?? null }));
}

/** @param {NodeFormProps} props */
export function UpdateStageForm({ node, problems, onConfig }) {
  const config = node.config;
  const reasonField = config.stage ? /** @type {Record<string, string>} */ (FLOW_STAGE_REASON_FIELDS)[config.stage] ?? null : null;
  const stageProblem = problems.find((problem) => problem.code === 'STAGE_NOT_FOUND' || problem.code === 'STAGE_TARGET_NOT_ALLOWED');
  const reasonProblem = problems.find((problem) => problem.code === 'STAGE_REASON_REQUIRED');
  return html`<div class="flow-node-form" data-testid="flow-update-stage-form">
    <fieldset class="flow-fieldset" data-invalid=${fieldProblemText(problems, 'stage') || stageProblem ? 'true' : undefined}>
      <legend class="flow-field-label">${t('flows.forms.stage.label')}</legend>
      <div class="flow-stage-options" role="radiogroup" aria-label=${t('flows.forms.stage.label')}>
        ${stageOptions().map((option) => {
          const isBlocked = FLOW_BLOCKED_TARGET_STAGES.includes(option.value);
          const isChecked = config.stage === option.value;
          return html`<label key=${option.value} class="flow-stage-option" data-checked=${isChecked ? 'true' : undefined} data-disabled=${isBlocked ? 'true' : undefined}
            title=${isBlocked ? t('flows.forms.stage.enrolledBlocked') : undefined}>
            <input type="radio" name=${`flow-stage-${node.id}`} value=${option.value} checked=${isChecked} disabled=${isBlocked}
              onChange=${() => onConfig({ stage: option.value, lostReason: null, lostReasonDetail: null, notLeadReason: null })} />
            <${Tag} color=${option.color ?? 'gray'} text=${option.label} />
            ${isBlocked ? html`<span class="flow-field-hint">${t('flows.forms.stage.enrolledBlocked')}</span>` : null}
          </label>`;
        })}
      </div>
      ${fieldProblemText(problems, 'stage') || stageProblem
        ? html`<span class="flow-field-error" role="alert">${fieldProblemText(problems, 'stage') ?? flowProblemText(/** @type {any} */ (stageProblem))}</span>`
        : null}
    </fieldset>
    ${reasonField
      ? html`<${FlowField} label=${reasonField === 'lostReason' ? t('flows.forms.stage.lostReason') : t('flows.forms.stage.notLeadReason')}
          htmlFor=${`flow-stage-reason-${node.id}`} error=${reasonProblem ? t('flows.panel.required') : null}>
          <select id=${`flow-stage-reason-${node.id}`} class="text-input" value=${config[reasonField] ?? ''}
            onChange=${(/** @type {Event} */ event) => onConfig({ [reasonField]: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
            <option value="">${t('flows.forms.choose')}</option>
            ${selectOptions(reasonField).map((option) => html`<option key=${option.value} value=${option.value}>${option.label}</option>`)}
          </select>
        <//>`
      : null}
    ${reasonField === 'lostReason'
      ? html`<${FlowField} label=${t('flows.forms.stage.detail')} htmlFor=${`flow-stage-detail-${node.id}`}>
          <input id=${`flow-stage-detail-${node.id}`} class="text-input" value=${config.lostReasonDetail ?? ''} maxLength="2000"
            onInput=${(/** @type {InputEvent} */ event) => onConfig({ lostReasonDetail: /** @type {HTMLInputElement} */ (event.currentTarget).value || null })} />
        <//>`
      : null}
    <p class="flow-field-hint">${t('flows.forms.stage.requirementsHint')}</p>
  </div>`;
}

/** @param {NodeFormProps} props */
export function TagsForm({ node, problems, onConfig }) {
  const config = node.config;
  const values = /** @type {string[]} */ (config.values ?? []);
  const options = selectOptions(config.fieldName ?? 'tags');
  const isFull = values.length >= MAXIMUM_FLOW_TAGS_PER_ACTION;
  const optionProblem = problems.find((problem) => problem.code === 'OPTION_NOT_FOUND' || problem.code === 'TAGS_TOO_MANY' || problem.code === 'LIMIT_ITEMS');
  return html`<div class="flow-node-form" data-testid="flow-tags-form">
    <fieldset class="flow-fieldset" data-invalid=${fieldProblemText(problems, 'values') || optionProblem ? 'true' : undefined}>
      <legend class="flow-field-label">${translateMetadataLabel(cardField(config.fieldName ?? 'tags')?.label ?? '') || t('flows.forms.tags.label')}</legend>
      <div class="flow-tag-options" role="group">
        ${options.map((option) => {
          const isChecked = values.includes(option.value);
          return html`<button type="button" key=${option.value} class="flow-tag-option" aria-pressed=${isChecked ? 'true' : 'false'} disabled=${!isChecked && isFull}
            onClick=${() => onConfig({ values: isChecked ? values.filter((value) => value !== option.value) : [...values, option.value] })}>
            <${Tag} color=${option.color ?? 'gray'} text=${option.label} />
          </button>`;
        })}
      </div>
      <span class="flow-field-hint">${t('flows.forms.counter', { count: values.length, maximum: MAXIMUM_FLOW_TAGS_PER_ACTION })}</span>
      ${fieldProblemText(problems, 'values') || optionProblem
        ? html`<span class="flow-field-error" role="alert">${fieldProblemText(problems, 'values') ?? flowProblemText(/** @type {any} */ (optionProblem))}</span>`
        : null}
    </fieldset>
  </div>`;
}

/** @param {NodeFormProps} props */
export function AssignOwnerForm({ node, problems, onConfig }) {
  const config = node.config;
  const members = (workspaceMembersState.value ?? []).map((member) => ({ id: member.id, label: memberDisplayName(member) }))
    .sort((first, second) => first.label.localeCompare(second.label, 'pt-BR'));
  const isRoundRobin = config.strategy === 'ROUND_ROBIN';
  const memberProblem = problems.find((problem) => problem.code === 'MEMBER_UNAVAILABLE');
  const name = `flow-owner-strategy-${node.id}`;
  return html`<div class="flow-node-form" data-testid="flow-assign-owner-form">
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.forms.owner.strategy')}>
      <${FlowRadioOption} name=${name} value="SPECIFIC" checked=${!isRoundRobin} title=${t('flows.forms.owner.specific')}
        onSelect=${() => onConfig({ strategy: 'SPECIFIC', workspaceMemberIds: (config.workspaceMemberIds ?? []).slice(0, 1) })} />
      <${FlowRadioOption} name=${name} value="ROUND_ROBIN" checked=${isRoundRobin} title=${t('flows.forms.owner.roundRobin')}
        description=${t('flows.forms.owner.roundRobinHint')} onSelect=${() => onConfig({ strategy: 'ROUND_ROBIN' })} />
    </div>
    ${isRoundRobin
      ? html`<${FlowField} label=${t('flows.forms.owner.members')} error=${fieldProblemText(problems, 'workspaceMemberIds') ?? (memberProblem ? flowProblemText(memberProblem) : null)}
          hint=${(config.workspaceMemberIds ?? []).length === 0 ? t('flows.forms.owner.wholeTeam') : null}>
          <${FlowChecklist} items=${members} selected=${config.workspaceMemberIds ?? []} maximum=${MAXIMUM_ASSIGN_OWNER_MEMBERS} label=${t('flows.forms.owner.members')}
            onChange=${(/** @type {string[]} */ workspaceMemberIds) => onConfig({ workspaceMemberIds })} />
        <//>`
      : html`<${FlowField} label=${t('flows.forms.owner.member')} htmlFor=${`flow-owner-member-${node.id}`}
          error=${fieldProblemText(problems, 'workspaceMemberIds') ?? (memberProblem ? flowProblemText(memberProblem) : null)}>
          <select id=${`flow-owner-member-${node.id}`} class="text-input" value=${config.workspaceMemberIds?.[0] ?? ''}
            onChange=${(/** @type {Event} */ event) => {
              const value = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
              onConfig({ workspaceMemberIds: value ? [value] : [] });
            }}>
            <option value="">${t('flows.forms.choose')}</option>
            ${members.map((member) => html`<option key=${member.id} value=${member.id}>${member.label}</option>`)}
          </select>
        <//>`}
    <label class="flow-checkbox-row">
      <${Checkbox} checked=${config.onlyIfEmpty === true} onChange=${(/** @type {boolean} */ onlyIfEmpty) => onConfig({ onlyIfEmpty })} ariaLabel=${t('flows.forms.owner.onlyIfEmpty')} />
      <span>${t('flows.forms.owner.onlyIfEmpty')}</span>
    </label>
  </div>`;
}
